import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Edit, Plus, Trash2, Eye, Save } from 'lucide-react';
import axios from 'axios';

const CMSPageEditor = ({ BACKEND_URL }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [showCreatePageDialog, setShowCreatePageDialog] = useState(false);
  const [newPage, setNewPage] = useState({
    title: '',
    slug: '',
    position: 0,
    showInNav: true
  });

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/pages`);
      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast({
        title: "Error",
        description: "Failed to load pages",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePage = async () => {
    if (!newPage.title || !newPage.slug) {
      toast({
        title: "Error",
        description: "Please enter page title and slug",
        variant: "destructive"
      });
      return;
    }

    try {
      const pageData = {
        page_id: newPage.slug,
        title: newPage.title,
        slug: newPage.slug,
        published: true,
        position: newPage.position,
        showInNav: newPage.showInNav,
        sections: {
          hero: {
            title: newPage.title,
            subtitle: 'Welcome to ' + newPage.title,
            description: 'Add your content here'
          }
        }
      };

      const response = await axios.post(`${BACKEND_URL}/api/admin/cms/page`, pageData);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Page created successfully"
        });
        setShowCreatePageDialog(false);
        setNewPage({ title: '', slug: '', position: 0, showInNav: true });
        fetchPages();
      }
    } catch (error) {
      console.error('Error creating page:', error);
      toast({
        title: "Error",
        description: "Failed to create page",
        variant: "destructive"
      });
    }
  };

  const handleDeletePage = async (slug) => {
    if (!window.confirm(`Are you sure you want to delete the page "${slug}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${BACKEND_URL}/api/admin/cms/page/${slug}`);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Page deleted successfully"
        });
        fetchPages();
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      toast({
        title: "Error",
        description: "Failed to delete page",
        variant: "destructive"
      });
    }
  };

  const handleEditPage = async (slug) => {
    setEditorLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/page/${slug}`);
      console.log('Loading page:', response.data);
      
      if (response.data && !response.data.error) {
        setEditingPage(response.data);
      } else {
        // If page not found, use default structure
        setEditingPage({
          page_id: slug,
          title: slug,
          slug: slug,
          published: true,
          sections: {
            hero: {
              title: slug,
              subtitle: 'Welcome',
              description: 'Add your content here'
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading page:', error);
      setEditingPage({
        page_id: slug,
        title: slug,
        slug: slug,
        published: true,
        sections: {
          hero: {
            title: slug,
            subtitle: 'Welcome',
            description: 'Add your content here'
          }
        }
      });
    } finally {
      setEditorLoading(false);
      setTimeout(() => {
        setShowEditor(true);
      }, 100);
    }
  };

  const addNewSection = (sectionType) => {
    const newSections = { ...editingPage.sections };
    
    const sectionTemplates = {
      textBlock: {
        title: 'New Text Block',
        content: 'Add your content here'
      },
      imageText: {
        title: 'Image & Text Section',
        content: 'Add your content here',
        imageUrl: 'https://via.placeholder.com/400x300'
      },
      features: {
        title: 'Features',
        items: [
          { title: 'Feature 1', description: 'Description', icon: '✨' },
          { title: 'Feature 2', description: 'Description', icon: '🚀' },
          { title: 'Feature 3', description: 'Description', icon: '💡' }
        ]
      },
      cta: {
        title: 'Call to Action',
        subtitle: 'Get Started Today',
        buttonText: 'Learn More',
        buttonLink: '#'
      }
    };

    const sectionKey = `section_${Date.now()}`;
    newSections[sectionKey] = {
      type: sectionType,
      ...sectionTemplates[sectionType]
    };

    setEditingPage({
      ...editingPage,
      sections: newSections
    });

    toast({
      title: "Section Added",
      description: `New ${sectionType} section added`
    });
  };

  const removeSection = (sectionKey) => {
    if (!window.confirm('Are you sure you want to remove this section?')) {
      return;
    }

    const newSections = { ...editingPage.sections };
    delete newSections[sectionKey];

    setEditingPage({
      ...editingPage,
      sections: newSections
    });

    toast({
      title: "Section Removed",
      description: "Section has been removed"
    });
  };

  const handleEditAboutUs = async () => {
    setEditorLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/page/about-us`);
      console.log('API Response:', response.data);
      
      if (response.data && !response.data.error && response.data.sections) {
        console.log('Using API data');
        setEditingPage(response.data);
      } else {
        // Create default About Us page
        console.log('Using default content');
        const defaultContent = {
          page_id: 'about-us',
          title: 'About Us',
          slug: 'about-us',
          published: true,
          sections: getDefaultAboutUsContent()
        };
        console.log('Default content:', defaultContent);
        setEditingPage(defaultContent);
      }
    } catch (error) {
      console.error('Error loading page:', error);
      // Set default content on error
      const defaultContent = {
        page_id: 'about-us',
        title: 'About Us',
        slug: 'about-us',
        published: true,
        sections: getDefaultAboutUsContent()
      };
      console.log('Error - using default content:', defaultContent);
      setEditingPage(defaultContent);
    } finally {
      setEditorLoading(false);
      // Small delay to ensure state is set
      setTimeout(() => {
        setShowEditor(true);
      }, 100);
    }
  };

  const getDefaultAboutUsContent = () => ({
    hero: {
      title: 'About College Kampus',
      subtitle: 'Your trusted partner in college admission predictions',
      description: 'College Kampus is India\'s leading JEE college predictor platform. We help thousands of students make informed decisions about their college admissions with AI-powered predictions, comprehensive data analysis, and expert guidance. Our mission is to simplify the college selection process and ensure every student finds their perfect academic match.'
    },
    mission: {
      title: 'Our Mission',
      content: 'To empower every aspiring engineer with accurate, data-driven college admission predictions and help them achieve their educational dreams. We believe that every student deserves access to reliable information and tools that can guide them toward the right college and career path.'
    },
    vision: {
      title: 'Our Vision',
      content: 'To become India\'s most trusted and comprehensive college prediction platform, helping millions of students nationwide find their perfect college match. We envision a future where college admissions are transparent, accessible, and stress-free for all students and their families.'
    },
    services: {
      title: 'Our Services',
      items: [
        {
          title: 'JEE Rank Predictions',
          description: 'Get accurate admission predictions for IITs, NITs, IIITs, GFTIs, and top private engineering colleges based on your JEE Main and JEE Advanced ranks. Our algorithm analyzes 5+ years of cutoff data.',
          icon: '🎓'
        },
        {
          title: 'Historical Data Analysis',
          description: 'Access comprehensive 5-year cutoff trends, seat availability, and admission statistics for thousands of colleges across India. Make informed decisions with data-backed insights.',
          icon: '📊'
        },
        {
          title: 'Personalized PDF Reports',
          description: 'Receive detailed, customized PDF reports with your college predictions, cutoff comparisons, and admission probability analysis directly in your email inbox.',
          icon: '📄'
        },
        {
          title: 'Referral Rewards Program',
          description: 'Earn ₹200 for every friend you refer who uses our services. Share the benefits of accurate college predictions with your peers and get rewarded for helping them succeed.',
          icon: '🤝'
        }
      ]
    },
    history: {
      title: 'Our Story',
      content: 'Founded in 2024 in Chandigarh, College Kampus was born from a simple yet powerful idea: every student deserves transparent and accurate information about college admissions. What started as a passion project by education technology enthusiasts has quickly grown into one of India\'s most trusted college prediction platforms. Today, we serve thousands of students across the country, helping them navigate the complex world of engineering admissions with confidence. Our team of data scientists, educators, and technologists work tirelessly to ensure our predictions are accurate, our platform is user-friendly, and our support is always available when students need us most.'
    },
    team: {
      title: 'Our Team',
      description: 'Meet the dedicated professionals behind College Kampus',
      members: [
        {
          name: 'Rajesh Kumar',
          role: 'Founder & CEO',
          description: 'Education technology expert with 10+ years of experience in college admissions consulting and data analytics'
        },
        {
          name: 'Priya Sharma',
          role: 'Chief Technology Officer',
          description: 'AI and machine learning specialist focused on predictive algorithms and data science for education'
        },
        {
          name: 'Amit Patel',
          role: 'Head of Operations',
          description: 'Operations leader ensuring seamless customer experience and platform reliability'
        }
      ]
    },
    contact: {
      title: 'Get In Touch',
      email: 'contact@collegekampus.com',
      phone: '+91 9056161621',
      address: 'Chandigarh, India',
      socialLinks: {
        facebook: 'https://facebook.com/collegekampus',
        twitter: 'https://twitter.com/collegekampus',
        instagram: 'https://instagram.com/collegekampus',
        linkedin: 'https://linkedin.com/company/collegekampus'
      }
    },
    companyInfo: {
      title: 'Company Information',
      founded: '2024',
      headquarters: 'Chandigarh, India',
      employees: '10-50 professionals',
      website: 'www.collegekampus.com'
    },
    footer: {
      copyrightText: '© 2024 College Kampus. All rights reserved.',
      links: [
        { text: 'Privacy Policy', url: '/privacy' },
        { text: 'Terms of Service', url: '/terms' },
        { text: 'Contact Us', url: '/about-us' }
      ]
    }
  });

  const handleSavePage = async () => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/cms/page/${editingPage.slug}`,
        editingPage
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Page updated successfully"
        });
        fetchPages();
        setShowEditor(false);
      } else {
        // If update fails, try creating the page
        const createResponse = await axios.post(
          `${BACKEND_URL}/api/admin/cms/page`,
          editingPage
        );
        
        if (createResponse.data.success) {
          toast({
            title: "Success",
            description: "Page created successfully"
          });
          fetchPages();
          setShowEditor(false);
        }
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast({
        title: "Error",
        description: "Failed to save page",
        variant: "destructive"
      });
    }
  };

  const updateSection = (sectionKey, field, value) => {
    setEditingPage({
      ...editingPage,
      sections: {
        ...editingPage.sections,
        [sectionKey]: {
          ...editingPage.sections[sectionKey],
          [field]: value
        }
      }
    });
  };

  const updateServiceItem = (index, field, value) => {
    const updatedItems = [...editingPage.sections.services.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setEditingPage({
      ...editingPage,
      sections: {
        ...editingPage.sections,
        services: {
          ...editingPage.sections.services,
          items: updatedItems
        }
      }
    });
  };

  const updateTeamMember = (index, field, value) => {
    const updatedMembers = [...editingPage.sections.team.members];
    updatedMembers[index] = {
      ...updatedMembers[index],
      [field]: value
    };
    setEditingPage({
      ...editingPage,
      sections: {
        ...editingPage.sections,
        team: {
          ...editingPage.sections.team,
          members: updatedMembers
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>CMS Page Management</CardTitle>
              <CardDescription>Manage website pages and content</CardDescription>
            </div>
            <Button onClick={() => setShowCreatePageDialog(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Create New Page
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Loading pages...</p>
              </div>
            ) : (
              <>
                {/* About Us Page (Default) */}
                <div className="border rounded-lg p-4 flex items-center justify-between bg-blue-50">
                  <div>
                    <h3 className="font-semibold text-lg">About Us Page</h3>
                    <p className="text-sm text-slate-600">Manage the About Us page content and sections</p>
                    <span className="text-xs text-blue-600 font-medium">Default Page</span>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleEditAboutUs} className="gap-2">
                      <Edit className="w-4 h-4" />
                      Edit Page
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open('/about-us', '_blank')}
                      className="gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </Button>
                  </div>
                </div>

                {/* Custom Pages */}
                {pages.filter(p => p.slug !== 'about-us').map((page) => (
                  <div key={page.slug} className="border rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-lg">{page.title}</h3>
                      <p className="text-sm text-slate-600">/{page.slug}</p>
                      <div className="flex gap-2 mt-2">
                        <span className={`text-xs px-2 py-1 rounded ${page.showInNav ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {page.showInNav ? 'In Navigation' : 'Hidden'}
                        </span>
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                          Position: {page.position || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => handleEditPage(page.slug)} size="sm" className="gap-2">
                        <Edit className="w-4 h-4" />
                        Edit
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(`/${page.slug}`, '_blank')}
                        className="gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => handleDeletePage(page.slug)}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}

                {pages.filter(p => p.slug !== 'about-us').length === 0 && (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                    <p className="text-gray-600">No custom pages yet. Create your first page!</p>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Page Dialog */}
      <Dialog open={showCreatePageDialog} onOpenChange={setShowCreatePageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Page</DialogTitle>
            <DialogDescription>
              Add a new page to your website
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Page Title</Label>
              <Input
                value={newPage.title}
                onChange={(e) => setNewPage({ ...newPage, title: e.target.value })}
                placeholder="e.g., Services"
              />
            </div>
            <div>
              <Label>Page Slug (URL)</Label>
              <Input
                value={newPage.slug}
                onChange={(e) => setNewPage({ ...newPage, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                placeholder="e.g., services"
              />
              <p className="text-xs text-gray-500 mt-1">URL will be: /{newPage.slug}</p>
            </div>
            <div>
              <Label>Navigation Position</Label>
              <Input
                type="number"
                value={newPage.position}
                onChange={(e) => setNewPage({ ...newPage, position: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first in navigation</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newPage.showInNav}
                onChange={(e) => setNewPage({ ...newPage, showInNav: e.target.checked })}
                className="w-4 h-4"
              />
              <Label>Show in navigation menu</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreatePageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreatePage} className="gap-2">
              <Plus className="w-4 h-4" />
              Create Page
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Page: {editingPage?.title || 'Loading...'}</DialogTitle>
            <DialogDescription>
              Customize all sections of this page
            </DialogDescription>
          </DialogHeader>

          {editorLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading page content...</p>
              </div>
            </div>
          ) : editingPage ? (
            <div className="space-y-6">
              {/* Add New Section */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-purple-50">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New Section
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => addNewSection('textBlock')}
                    className="gap-2"
                  >
                    📝 Text Block
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => addNewSection('imageText')}
                    className="gap-2"
                  >
                    🖼️ Image & Text
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => addNewSection('features')}
                    className="gap-2"
                  >
                    ✨ Features Grid
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => addNewSection('cta')}
                    className="gap-2"
                  >
                    🎯 Call to Action
                  </Button>
                </div>
              </div>
              
              {/* Existing Sections */}
              {Object.entries(editingPage.sections || {}).map(([sectionKey, section]) => (
                <div key={sectionKey} className="border-b pb-6 relative">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold capitalize">{sectionKey.replace(/_/g, ' ')}</h3>
                    {!['hero', 'mission', 'vision', 'services', 'history', 'team', 'contact', 'companyInfo', 'footer'].includes(sectionKey) && (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => removeSection(sectionKey)}
                        className="gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </Button>
                    )}
                  </div>
                  
                  {/* Render section fields based on type */}
                  {sectionKey === 'hero' && (
                    <div className="space-y-3">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={editingPage.sections.hero?.title || ''}
                          onChange={(e) => updateSection('hero', 'title', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Subtitle</Label>
                        <Input
                          value={editingPage.sections.hero?.subtitle || ''}
                          onChange={(e) => updateSection('hero', 'subtitle', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <textarea
                          className="w-full border rounded-md p-2 min-h-[80px]"
                          value={editingPage.sections.hero?.description || ''}
                          onChange={(e) => updateSection('hero', 'description', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Mission & Vision */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Mission & Vision</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Label>Mission Title</Label>
                    <Input
                      value={editingPage.sections.mission?.title || ''}
                      onChange={(e) => updateSection('mission', 'title', e.target.value)}
                    />
                    <Label>Mission Content</Label>
                    <textarea
                      className="w-full border rounded-md p-2 min-h-[100px]"
                      value={editingPage.sections.mission?.content || ''}
                      onChange={(e) => updateSection('mission', 'content', e.target.value)}
                    />
                  </div>
                  <div className="space-y-3">
                    <Label>Vision Title</Label>
                    <Input
                      value={editingPage.sections.vision?.title || ''}
                      onChange={(e) => updateSection('vision', 'title', e.target.value)}
                    />
                    <Label>Vision Content</Label>
                    <textarea
                      className="w-full border rounded-md p-2 min-h-[100px]"
                      value={editingPage.sections.vision?.content || ''}
                      onChange={(e) => updateSection('vision', 'content', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Services</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={editingPage.sections.services?.title || ''}
                      onChange={(e) => updateSection('services', 'title', e.target.value)}
                    />
                  </div>
                  {editingPage.sections.services?.items?.map((service, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <h4 className="font-semibold">Service {index + 1}</h4>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label>Icon</Label>
                          <Input
                            value={service.icon}
                            onChange={(e) => updateServiceItem(index, 'icon', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label>Title</Label>
                          <Input
                            value={service.title}
                            onChange={(e) => updateServiceItem(index, 'title', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <textarea
                          className="w-full border rounded-md p-2 min-h-[60px]"
                          value={service.description}
                          onChange={(e) => updateServiceItem(index, 'description', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* History */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">History</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={editingPage.sections.history?.title || ''}
                      onChange={(e) => updateSection('history', 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Content</Label>
                    <textarea
                      className="w-full border rounded-md p-2 min-h-[100px]"
                      value={editingPage.sections.history?.content || ''}
                      onChange={(e) => updateSection('history', 'content', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Team */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Team</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={editingPage.sections.team?.title || ''}
                      onChange={(e) => updateSection('team', 'title', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={editingPage.sections.team?.description || ''}
                      onChange={(e) => updateSection('team', 'description', e.target.value)}
                    />
                  </div>
                  {editingPage.sections.team?.members?.map((member, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <h4 className="font-semibold">Team Member {index + 1}</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Name</Label>
                          <Input
                            value={member?.name || ''}
                            onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Role</Label>
                          <Input
                            value={member?.role || ''}
                            onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={member?.description || ''}
                          onChange={(e) => updateTeamMember(index, 'description', e.target.value)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact & Company Info */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={editingPage.sections.contact?.email || ''}
                      onChange={(e) => updateSection('contact', 'email', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={editingPage.sections.contact?.phone || ''}
                      onChange={(e) => updateSection('contact', 'phone', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Address</Label>
                    <Input
                      value={editingPage.sections.contact?.address || ''}
                      onChange={(e) => updateSection('contact', 'address', e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="mt-6">
                  <h4 className="text-md font-semibold mb-3">Social Media Links</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Facebook URL</Label>
                      <Input
                        value={editingPage.sections.contact?.socialLinks?.facebook || ''}
                        onChange={(e) => setEditingPage({
                          ...editingPage,
                          sections: {
                            ...editingPage.sections,
                            contact: {
                              ...editingPage.sections.contact,
                              socialLinks: {
                                ...editingPage.sections.contact.socialLinks,
                                facebook: e.target.value
                              }
                            }
                          }
                        })}
                        placeholder="https://facebook.com/collegekampus"
                      />
                    </div>
                    <div>
                      <Label>Twitter URL</Label>
                      <Input
                        value={editingPage.sections.contact?.socialLinks?.twitter || ''}
                        onChange={(e) => setEditingPage({
                          ...editingPage,
                          sections: {
                            ...editingPage.sections,
                            contact: {
                              ...editingPage.sections.contact,
                              socialLinks: {
                                ...editingPage.sections.contact.socialLinks,
                                twitter: e.target.value
                              }
                            }
                          }
                        })}
                        placeholder="https://twitter.com/collegekampus"
                      />
                    </div>
                    <div>
                      <Label>Instagram URL</Label>
                      <Input
                        value={editingPage.sections.contact?.socialLinks?.instagram || ''}
                        onChange={(e) => setEditingPage({
                          ...editingPage,
                          sections: {
                            ...editingPage.sections,
                            contact: {
                              ...editingPage.sections.contact,
                              socialLinks: {
                                ...editingPage.sections.contact.socialLinks,
                                instagram: e.target.value
                              }
                            }
                          }
                        })}
                        placeholder="https://instagram.com/collegekampus"
                      />
                    </div>
                    <div>
                      <Label>LinkedIn URL</Label>
                      <Input
                        value={editingPage.sections.contact?.socialLinks?.linkedin || ''}
                        onChange={(e) => setEditingPage({
                          ...editingPage,
                          sections: {
                            ...editingPage.sections,
                            contact: {
                              ...editingPage.sections.contact,
                              socialLinks: {
                                ...editingPage.sections.contact.socialLinks,
                                linkedin: e.target.value
                              }
                            }
                          }
                        })}
                        placeholder="https://linkedin.com/company/collegekampus"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Company Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Founded</Label>
                    <Input
                      value={editingPage.sections.companyInfo?.founded || ''}
                      onChange={(e) => updateSection('companyInfo', 'founded', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Headquarters</Label>
                    <Input
                      value={editingPage.sections.companyInfo?.headquarters || ''}
                      onChange={(e) => updateSection('companyInfo', 'headquarters', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Team Size</Label>
                    <Input
                      value={editingPage.sections.companyInfo?.employees || ''}
                      onChange={(e) => updateSection('companyInfo', 'employees', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={editingPage.sections.companyInfo?.website || ''}
                      onChange={(e) => updateSection('companyInfo', 'website', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Footer Section */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold mb-4">Footer Settings</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Copyright Text</Label>
                    <Input
                      value={editingPage.sections.footer?.copyrightText || '© 2024 College Kampus. All rights reserved.'}
                      onChange={(e) => updateSection('footer', 'copyrightText', e.target.value)}
                      placeholder="© 2024 College Kampus. All rights reserved."
                    />
                  </div>
                  
                  <div>
                    <h4 className="text-md font-semibold mb-3">Footer Links</h4>
                    {(editingPage.sections.footer?.links || [
                      { text: 'Privacy Policy', url: '/privacy' },
                      { text: 'Terms of Service', url: '/terms' },
                      { text: 'Contact Us', url: '/about-us' }
                    ]).map((link, index) => (
                      <div key={index} className="grid grid-cols-2 gap-3 mb-3 p-3 border rounded-lg">
                        <div>
                          <Label className="text-xs">Link Text</Label>
                          <Input
                            value={link.text}
                            onChange={(e) => {
                              const updatedLinks = [...(editingPage.sections.footer?.links || [])];
                              updatedLinks[index] = { ...updatedLinks[index], text: e.target.value };
                              setEditingPage({
                                ...editingPage,
                                sections: {
                                  ...editingPage.sections,
                                  footer: {
                                    ...editingPage.sections.footer,
                                    links: updatedLinks
                                  }
                                }
                              });
                            }}
                            placeholder="Privacy Policy"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">URL</Label>
                          <Input
                            value={link.url}
                            onChange={(e) => {
                              const updatedLinks = [...(editingPage.sections.footer?.links || [])];
                              updatedLinks[index] = { ...updatedLinks[index], url: e.target.value };
                              setEditingPage({
                                ...editingPage,
                                sections: {
                                  ...editingPage.sections,
                                  footer: {
                                    ...editingPage.sections.footer,
                                    links: updatedLinks
                                  }
                                }
                              });
                            }}
                            placeholder="/privacy"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditor(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSavePage} className="gap-2">
                  <Save className="w-4 h-4" />
                  Save Changes
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              <p>No content available. Please try again.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSPageEditor;
